import React from "react";
import { Loader2, X } from "lucide-react";
import clsx from "clsx";

interface ModalProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onClose: () => void;
  scroll?: boolean;
}

export const Modal = ({
  title,
  subtitle,
  children,
  onClose,
  scroll = false,
}: ModalProps) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 p-4">
    <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
      <div className="flex items-start justify-between border-b border-slate-200 px-8 py-5">
        <div>
          <h2 className="text-xl font-bold">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-slate-200 p-3 text-slate-500 hover:bg-slate-50"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div
        className={`space-y-6 px-8 py-7 ${scroll ? "max-h-[70vh] overflow-y-auto" : ""}`}
      >
        {children}
      </div>
    </div>
  </div>
);


export const ModalActions = ({
  label,
  isSaving,
  onCancel,
  onSave,
  disabled,
  
}: {
  label: string;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
  disabled?:boolean,
  

  
  
}) => (
  
  <div className=" flex  justify-between gap-2  border-t border-slate-200 px-0 py-5">
    <button
      type="button"
      onClick={onCancel}
      className="h-12 flex-1 rounded-lg border border-slate-200 font-semibold text-slate-600"
    >
      Cancel
    </button>
    <button
      type="button"
      onClick={onSave}
      disabled={isSaving || disabled}  
      className={`flex h-12 flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 font-semibold text-white hover:bg-blue-700 disabled:opacity-60
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
      {label}
    </button>
  </div>
);

export const InfoNotice = ({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "blue" | "orange";
}) => (
  <div
    className={`rounded-lg border px-5 py-4 text-sm ${
      tone === "blue"
        ? "border-sky-200 bg-sky-50 text-sky-900"
        : "border-orange-200 bg-orange-50 text-orange-900"
    }`}
  >
    {children}
  </div>
);

export const FormField = ({
  label,
  required,
  children,
  className = "",
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`block ${className}`}>
    <span className="mb-2 block text-sm font-semibold text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </span>
    {children}
  </div>
);

export const TextInput = ({
  label,
  required,
  value,
  onChange,
  placeholder,
  className = "",
  inputClassCss = "",
  type = "text",
  disabled = false,
  min,
  max,
}: {
  label: string;
  required?: boolean;
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassCss?: string;
  type?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
}) => (
  <FormField label={label} required={required} className={className}>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className={clsx(
        inputClassName,
        inputClassCss,
        disabled && "cursor-not-allowed bg-slate-50 text-slate-500",
      )}
      disabled={disabled}
      min={type === "number" ? (min ?? 1) : undefined}
      max={type === "number" ? max : undefined}
    />
  </FormField>
);

export const ConfirmDeleteModal = ({
  label,
  onCancel,
  onConfirm,
  loading,
}: {
  label: string;
  onCancel: () => void;
  onConfirm: () => void;
  loading?: boolean;
}) => {
  let message: string = `Are you sure you want to delete this ${label?.toLowerCase()}? Once deleted, the ${label?.toLowerCase()} and its associated data cannot be recovered.`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="text-lg font-bold">Delete {label}?</h2>
        <p className="mt-2 text-slate-600">{message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-200 px-4 py-2 font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="rounded-lg bg-red-600 px-4 py-2 font-semibold text-white"
          >
            {loading ? <Loader2 className="h-4 w-12 animate-spin" /> : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const inputClassName =
  "h-12 w-full rounded-lg border border-slate-200 px-4 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

export const toDateInputValue = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};
